import { DEFAULT } from '../../../shared/constants/defaults';
import { QUALIFICATIONS_CATEGORIES } from '../../../shared/constants/qualification-categories';
import { Common } from '../../../shared/domain/common';
import { AidaPayload } from '../../../shared/interfaces/aida.interface';
import { QuotaPayload } from '../../../shared/interfaces/quota.interface';
import { SurveyPayload } from '../../../shared/interfaces/survey-meta-data.interface';
import { AidaQualifications } from '../../../shared/services/aida/aida-payload/aida-payload.service';
import { SurveyStatus } from '../../../shared/utils/survey-status';
import { AidaQuotaFilter } from './aida-quota-filter/aida-quota-filter';

export class AidaSurveyPayloadTransformer {
  private _aida: AidaPayload;

  private _payload: SurveyPayload;

  private _nestedQuotas: QuotaPayload[] = [];

  private _nonNestedQuotas: QuotaPayload[] = [];

  private _quotas: QuotaPayload[] = [];

  private _isLaunchedSurvey: boolean = false;

  private _mappedDBQualificationData: AidaQualifications = {};

  constructor(
    surveyPayload: SurveyPayload,
    mappedDBQualificationCodes: AidaQualifications
  ) {
    this._payload = surveyPayload ?? {};
    this._mappedDBQualificationData = mappedDBQualificationCodes;

    const [, countryCode] = (surveyPayload.survey_localization || '').split(
      '_'
    );
    const { eligible = [], ineligible = [] } = new AidaQuotaFilter(
      surveyPayload?.click_balancing
        ? surveyPayload?.estimated_clicks!
        : surveyPayload?.completes_required,
      this._mappedDBQualificationData,
      countryCode,
      this._payload.quotas ?? []
    ).filter();
    this._quotas = eligible as QuotaPayload[];
    this._aida =
      ineligible.length || !surveyPayload?.aida
        ? DEFAULT.AIDA_PAYLOAD
        : surveyPayload?.aida;
    this._isLaunchedSurvey =
      !!this._payload?.ps_survey_status &&
      this._payload.ps_survey_status !== SurveyStatus.Draft;
    this._partitionQuotasAndSet();
  }

  public transform(): AidaPayload {
    if (!this._aida?.enabled || this._isLaunchedSurvey) {
      return this._aida;
    }

    this._setNestedQuotasToAida();
    this._setNonNestedQuotasToAida();
    this._disableAidaOnQuotaChange();

    return this._aida;
  }

  private _setNestedQuotasToAida() {
    if (!this._nestedQuotas.length) {
      return;
    }

    if (
      Common.isMultiNestedQuotasAvailable(this._nestedQuotas) &&
      this._payload.ps_survey_status === SurveyStatus.Draft
    ) {
      this._aida = DEFAULT.AIDA_PAYLOAD;
      return;
    }

    this._aida.selected_quota_ids = this._nestedQuotas.map(
      (quota) => quota.buyer_quota_id!
    );
  }

  private _setNonNestedQuotasToAida() {
    if (
      this._nestedQuotas.length ||
      this._aida.selected_quota_ids?.length ||
      !this._nonNestedQuotas.length
    ) {
      return;
    }
    const firstQualificationCode =
      this._nonNestedQuotas[0]?.criteria[0]?.qualification_code;
    this._aida.selected_quota_ids = this._nonNestedQuotas
      .filter(
        (quota) =>
          quota.criteria[0].qualification_code === firstQualificationCode
      )
      .map((quota) => quota.buyer_quota_id!);
  }

  private _disableAidaOnQuotaChange() {
    const quotas = this._aida?.selected_quota_ids ?? [];
    const isNestedSet = this._nestedQuotas.length
      ? this._nestedQuotas.every((quota) =>
          quotas.includes(quota.buyer_quota_id!)
        )
      : false;
    const selectedQuotas = this._nonNestedQuotas.length
      ? this._nonNestedQuotas.filter((quota) =>
          quotas.includes(quota.buyer_quota_id!)
        )
      : [];

    if (!isNestedSet && selectedQuotas.length !== quotas.length) {
      this._aida = {
        enabled: false,
        selected_quota_ids: [],
      };
    }
  }

  /**
   * Partitions quotas into nested and normal categories in a single iteration.
   * @returns {Array[]} - An array containing [nestedQuotas, nonNestedQuotas].
   */
  private _partitionQuotasAndSet() {
    const [nestedQuotas, nonNestedQuotas] = this._quotas.reduce(
      (result, quota) => {
        result[this._isNestedQuota(quota) ? 0 : 1].push(quota);
        return result;
      },
      [<QuotaPayload[]>[], <QuotaPayload[]>[]] // Initialize with two empty arrays: [nestedQuotas, normalQuotas]
    );
    this._nestedQuotas = nestedQuotas;
    this._nonNestedQuotas = nonNestedQuotas;
  }

  /**
   *
   * @param {Object} quota : Survey Quota Object
   * @returns {Boolean} : return is Quota nested or not
   */
  private _isNestedQuota(quota: QuotaPayload) {
    const { criteria = [] } = quota;
    return (
      (criteria.length > 1 &&
        quota.quota_category !== QUALIFICATIONS_CATEGORIES.ADVANCE) ||
      quota.quota_category === QUALIFICATIONS_CATEGORIES.NESTED
    );
  }
}
